/* eslint-disable react/no-array-index-key */
import * as styles from 'common/components/GenericModal/styles';
import { AddCircleOutline, Cancel } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { DEVICE_ACTIVATION_SWITCH_WARNING } from 'containers/HomePageContainer/components/PageConfigurator/constants';
import {
  FieldArray, useFormikContext,
} from 'formik';
import { LocalizedTextField } from 'containers/HomePageContainer/utils/LocalizedTextField';
import { SecondaryBannerFormData, SecondaryBannerItem } from 'containers/HomePageContainer/types/secondary-banner';
import { Switch } from 'common/components/Switch';
import { TextAndButtonAlignment } from 'containers/HomePageContainer/components/PageConfigurator/SecondaryBannerModalContent/field-type.enum';
import { TextField } from 'common/components/TextField';
import { getEnumValues } from 'utils/string/is-empty-or-undefined';
import { resolutionSuggestionLabel } from 'containers/HomePageContainer/components/PageConfigurator/SecondaryBannerModalContent/styles';
import { useGlobalAlertStore } from 'common/store/client/global-alert/use-global-alert.store';
import React, {
  ChangeEvent, FC, Fragment, useEffect, useState,
} from 'react';

interface DeviceData {
  key: 'desktop' | 'mobile';
}

const devicesData: DeviceData[] = [
  {
    key: 'desktop',
  },
  {
    key: 'mobile',
  }];

interface FieldToggle {
  desktop: string;
  mobile: string;
}

const srcToggle: FieldToggle = {
  desktop: 'en',
  mobile: 'en',
};

const altTexToggle: FieldToggle = {
  desktop: 'en',
  mobile: 'en',
};

const titleTexToggle: FieldToggle = {
  desktop: 'en',
  mobile: 'en',
};

const SubTitleTexToggle: FieldToggle = {
  desktop: 'en',
  mobile: 'en',
};

const buttonTexToggle: FieldToggle = {
  desktop: 'en',
  mobile: 'en',
};

const alignmentType: { name: string; value: string }[] = getEnumValues(TextAndButtonAlignment);
export const SecondaryBannersConfiguration:
FC = () => {
  const {
    values, setFieldValue, handleBlur, setValues, initialValues, errors, submitCount,
  } = useFormikContext<SecondaryBannerFormData>();
  const openAlert = useGlobalAlertStore((store) => store.openAlert);
  const [src, setSrc] = useState<FieldToggle>(srcToggle);
  const [altText, setAltText] = useState<FieldToggle>(altTexToggle);
  const [titleText, setTitleText] = useState<FieldToggle>(titleTexToggle);
  const [subTitleText, setSubTitleText] = useState<FieldToggle>(SubTitleTexToggle);
  const [buttonText, setButtonText] = useState<FieldToggle>(buttonTexToggle);

  const handleSrcTextLanguageChange = (key: string, newLang: string) => {
    setSrc((prevState) => ({
      ...prevState,
      [key]: newLang,
    }));
  };

  const handleAltTextLanguageChange = (key: string, newLang: string) => {
    setAltText((prevState) => ({
      ...prevState,
      [key]: newLang,
    }));
  };

  const handleTitleTextLanguageChange = (key: string, newLang: string) => {
    setTitleText((prevState) => ({
      ...prevState,
      [key]: newLang,
    }));
  };

  const handleSubTitleTextLanguageChange = (key: string, newLang: string) => {
    setSubTitleText((prevState) => ({
      ...prevState,
      [key]: newLang,
    }));
  };

  const handleButtonTextLanguageChange = (key: string, newLang: string) => {
    setButtonText((prevState) => ({
      ...prevState,
      [key]: newLang,
    }));
  };

  const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>, key: string) => {
    const isChecked = event.target.checked;
    if (isChecked || (values.content.desktop.available || values.content.mobile.available)) {
      setFieldValue(`content.${key}.available`, isChecked);
    } else {
      openAlert(DEVICE_ACTIVATION_SWITCH_WARNING);
    }
  };

  useEffect(() => {
    if (!initialValues.name) {
      setValues({
        content: {
          desktop: {
            available: true,
            items: [{
              bannerName: '', altText: '', src: '', frSrc: '', frAltText: '', titleText: '', frTitleText: '', subTitleText: '', frSubTitleText: '', buttonText: '', frButtonText: '', textColor: '', buttonTextColor: '', textAlignment: '', buttonAlignment: '', url: '', sequence: 0,
            }],
          },
          mobile: {
            available: true,
            items: [{
              bannerName: '', altText: '', src: '', frSrc: '', frAltText: '', titleText: '', frTitleText: '', subTitleText: '', frSubTitleText: '', buttonText: '', frButtonText: '', textColor: '', buttonTextColor: '', textAlignment: '', buttonAlignment: '', url: '', sequence: 0,
            }],
          },
        },
        name: values.name,
        activeTo: values.activeTo,
        activeFrom: values.activeFrom,
        sources: values.sources,
        position: values.position,
        pageType: values.pageType,
      }, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const widgetAlignment = {
    display: 'flex', justifyContent: 'flex-start', height: '100%', flexDirection: 'column',
  };

  return (
    <Grid container spacing={10}>
      {devicesData.map(({ key }) => {
        const disabled = !values.content[key].available;
        const deviceBanners = values.content[key].items;
        const itemErrorObject = key === 'desktop' ? errors.content?.desktop?.items : errors.content?.mobile?.items;
        const err: SecondaryBannerItem[] | undefined = Array.isArray(itemErrorObject)
          ? itemErrorObject as unknown as SecondaryBannerItem[] : undefined;

        return (
          <Grid key={key} item xs={6}>
            <Box sx={widgetAlignment}>
              <Switch
                name={`content.${key}.available`}
                label={key === 'desktop' ? 'Desktop' : 'Mobile'}
                labelPlacement="start"
                onChange={(event) => handleSwitchChange(event, key)}
                sx={styles.availabilitySwitch}
              />
              <FieldArray name={`content.${key}.items`}>
                {({
                  remove, push,
                }) => (
                  <Box sx={widgetAlignment}>
                    {deviceBanners.length > 0
                    && deviceBanners.map((_, index) => (
                      <Fragment key={`${key}-${index}`}>
                        <Box sx={styles.widgetOutline}>
                          {index !== 0 && (
                            <Button
                              onClick={() => remove(index)}
                              sx={styles.closeWidgetButton}
                            >
                              <Cancel fontSize="large" />
                            </Button>
                          )}
                          <TextField
                            name={`content.${key}.items[${index}].bannerName`}
                            disabled={disabled}
                            label="Banner Name"
                            sx={styles.textField}
                            onBlur={(e) => handleBlur(e)}
                          />
                          <LocalizedTextField
                            label="Image URL"
                            englishText={values.content[key].items[index].src || ''}
                            frenchText={values.content[key].items[index].frSrc || ''}
                            textLanguage={src[key]}
                            setTextLanguage={(newLang) => handleSrcTextLanguageChange(key, newLang)}
                            onBlur={(event) => {
                              if (src[key] === 'fr') {
                                setFieldValue(`content.${key}.items[${index}].frSrc`, event.target.value);
                              } else {
                                setFieldValue(`content.${key}.items[${index}].src`, event.target.value);
                              }
                            }}
                            disabled={disabled}
                            errors={
                              (submitCount > 0 && err && Array.isArray(err))
                                ? err[index].src : undefined
                            }
                          />
                          <TextField
                            name={`content.${key}.items[${index}].url`}
                            disabled={disabled}
                            label="Page Link URL"
                            sx={styles.textField}
                            onBlur={(e) => handleBlur(e)}
                          />
                          <LocalizedTextField
                            label="Alt Text"
                            englishText={values.content[key].items[index].altText || ''}
                            frenchText={values.content[key].items[index].frAltText || ''}
                            textLanguage={altText[key]}
                            setTextLanguage={(newLang) => handleAltTextLanguageChange(key, newLang)}
                            onBlur={(event) => {
                              if (altText[key] === 'fr') {
                                setFieldValue(`content.${key}.items[${index}].frAltText`, event.target.value);
                              } else {
                                setFieldValue(`content.${key}.items[${index}].altText`, event.target.value);
                              }
                            }}
                            disabled={disabled}
                            errors={
                              (submitCount > 0 && err && Array.isArray(err))
                                ? err[index].altText : undefined
                            }
                          />
                          <LocalizedTextField
                            label="Title Text"
                            englishText={values.content[key].items[index].titleText || ''}
                            frenchText={values.content[key].items[index].frTitleText || ''}
                            textLanguage={titleText[key]}
                            setTextLanguage={
                              (newLang) => handleTitleTextLanguageChange(key, newLang)
                            }
                            onBlur={(event) => {
                              if (titleText[key] === 'fr') {
                                setFieldValue(`content.${key}.items[${index}].frTitleText`, event.target.value);
                              } else {
                                setFieldValue(`content.${key}.items[${index}].titleText`, event.target.value);
                              }
                            }}
                            disabled={disabled}
                          />

                          <LocalizedTextField
                            label="Sub Tile Text"
                            englishText={values.content[key].items[index].subTitleText || ''}
                            frenchText={values.content[key].items[index].frSubTitleText || ''}
                            textLanguage={subTitleText[key]}
                            setTextLanguage={
                              (newLang) => handleSubTitleTextLanguageChange(key, newLang)
                            }
                            onBlur={(event) => {
                              if (subTitleText[key] === 'fr') {
                                setFieldValue(`content.${key}.items[${index}].frSubTitleText`, event.target.value);
                              } else {
                                setFieldValue(`content.${key}.items[${index}].subTitleText`, event.target.value);
                              }
                            }}
                            disabled={disabled}
                          />

                          <InputLabel>Text Color</InputLabel>
                          <Select
                            onChange={(event) => setFieldValue(`content.${key}.items[${index}].textColor`, event.target.value)}
                            name="textColor"
                            label="Text Color"
                            sx={{ width: '100%' }}
                            value={values.content[key].items[index].textColor}
                          >
                            <MenuItem value="red">Red</MenuItem>
                            <MenuItem value="white">White</MenuItem>
                          </Select>

                          <LocalizedTextField
                            label="Button Text"
                            englishText={values.content[key].items[index].buttonText || ''}
                            frenchText={values.content[key].items[index].frButtonText || ''}
                            textLanguage={buttonText[key]}
                            setTextLanguage={
                              (newLang) => handleButtonTextLanguageChange(key, newLang)
                            }
                            onBlur={(event) => {
                              if (subTitleText[key] === 'fr') {
                                setFieldValue(`content.${key}.items[${index}].frButtonText`, event.target.value);
                              } else {
                                setFieldValue(`content.${key}.items[${index}].buttonText`, event.target.value);
                              }
                            }}
                            disabled={disabled}
                          />
                          <InputLabel>Button Text Color</InputLabel>
                          <Select
                            onChange={(event) => setFieldValue(`content.${key}.items[${index}].buttonTextColor`, event.target.value)}
                            name="buttonTextColor"
                            label="Button Text Color"
                            sx={{ width: '100%' }}
                            value={values.content[key].items[index].buttonTextColor}
                          >
                            <MenuItem value="red">Red</MenuItem>
                            <MenuItem value="white">White</MenuItem>
                          </Select>
                          <Box mt={4} />
                          <InputLabel>Text Alignment</InputLabel>
                          <Select
                            onChange={(event) => setFieldValue(`content.${key}.items[${index}].textAlignment`, event.target.value)}
                            name="textAlignment"
                            label="Text Alignment"
                            sx={{ width: '100%' }}
                            value={values.content[key].items[index].textAlignment}
                          >
                            {alignmentType.map((alignmentKey) => (
                              <MenuItem key={alignmentKey.value} value={alignmentKey.value}>
                                {alignmentKey.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <InputLabel>Button Alignment</InputLabel>
                          <Select
                            onChange={(event) => setFieldValue(`content.${key}.items[${index}].buttonAlignment`, event.target.value)}
                            name="buttonAlignment"
                            label="Button Alignment"
                            sx={{ width: '100%' }}
                            value={values.content[key].items[index].buttonAlignment}
                          >
                            {alignmentType.map((alignmentKey) => (
                              <MenuItem key={alignmentKey.value} value={alignmentKey.value}>
                                {alignmentKey.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <Box sx={resolutionSuggestionLabel}>
                          {values.content.desktop.items.length > 1 && key === 'desktop' && <Typography>Recommended Image Resolution - 1028px X 640px</Typography>}
                          {values.content.desktop.items.length === 1 && key === 'desktop' && <Typography>Recommended Image Resolution - 2120px X 640px</Typography>}
                          {key === 'mobile' && <Typography>Recommended Image Resolution - 1456px X 528px</Typography>}
                        </Box>
                      </Fragment>
                    ))}
                    {deviceBanners.length < 2 && (
                      <Button
                        sx={{ fontSize: '18px' }}
                        onClick={() => push({
                          bannerName: '', altText: '', src: '', frSrc: '', frAltText: '', titleText: '', frTitleText: '', subTitleText: '', frSubTitleText: '', buttonText: '', frButtonText: '', textColor: '', buttonTextColor: '', textAlignment: '', buttonAlignment: '', url: '', sequence: 1,
                        })}
                      >
                        <AddCircleOutline fontSize="large" />
                        Add 2nd Banner
                      </Button>
                    )}
                  </Box>
                )}
              </FieldArray>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};
